import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../../contexts/AuthContext';

const statusPalette = {
  active: { label: 'Active', tone: 'badge-success' },
  suspended: { label: 'Suspended', tone: 'badge-danger' },
  inactive: { label: 'Inactive', tone: 'badge-muted' },
};

const PasswordVisibilityIcon = ({ visible }) => (
  <svg
    aria-hidden="true"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    {visible ? (
      <>
        <path
          d="M3 3L21 21"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M10.58 10.58C10.21 10.95 10 11.46 10 12C10 13.1 10.9 14 12 14C12.54 14 13.05 13.79 13.42 13.42"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M16.68 16.68C15.14 17.56 13.57 18 12 18C7 18 3.22 14.5 1.5 12C2.3 10.84 3.35 9.63 4.68 8.62"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M9.35 5.34C10.22 5.12 11.11 5 12 5C17 5 20.78 8.5 22.5 11.99C21.93 12.83 21.24 13.67 20.41 14.44"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    ) : (
      <>
        <path
          d="M1.5 12C3.22 8.5 7 5 12 5C17 5 20.78 8.5 22.5 12C20.78 15.5 17 19 12 19C7 19 3.22 15.5 1.5 12Z"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle
          cx="12"
          cy="12"
          r="3"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    )}
  </svg>
);

const UserList = () => {
  const { isSuperAdmin, isAdmin } = useAuth();
  const canEditRoles = isSuperAdmin();
  const canEditEmail = isAdmin();
  const canResetPasswords = isSuperAdmin();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [rolesError, setRolesError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [editingRolesUserId, setEditingRolesUserId] = useState(null);
  const [editingEmailUserId, setEditingEmailUserId] = useState(null);
  const [emailDraft, setEmailDraft] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);
  const [rolesDraft, setRolesDraft] = useState([]);
  const [rolesSaving, setRolesSaving] = useState(false);
  const [resetError, setResetError] = useState('');
  const [passwordModalUser, setPasswordModalUser] = useState(null);
  const [passwordDraft, setPasswordDraft] = useState('');
  const [passwordConfirmationDraft, setPasswordConfirmationDraft] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [showPasswordDraft, setShowPasswordDraft] = useState(false);
  const [showPasswordConfirmationDraft, setShowPasswordConfirmationDraft] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const requests = [axios.get('/admin/users')];
      if (canEditRoles) {
        requests.push(axios.get('/admin/roles'));
      }

      const responses = await Promise.all(requests);
      const usersRes = responses[0];
      const rolesRes = responses[1];

      setUsers(usersRes.data.data || usersRes.data);

      if (rolesRes) {
        setRoles(rolesRes.data.roles || rolesRes.data || []);
      }
    } catch (err) {
      setError('Unable to load users right now. Please retry in a moment.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEditRoles]);

  useEffect(() => {
    if (notice) {
      const timer = setTimeout(() => setNotice(''), 3500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [notice]);

  const startRolesEditing = (user) => {
    setEditingRolesUserId(user.id);
    setRolesDraft(user.roles?.map((role) => role.id) || []);
    setRolesError('');
  };

  const cancelRolesEditing = () => {
    setEditingRolesUserId(null);
    setRolesDraft([]);
    setRolesSaving(false);
    setRolesError('');
  };

  const toggleRoleDraft = (roleId) => {
    setRolesDraft((prev) =>
      prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId],
    );
  };

  const saveRoles = async () => {
    if (!editingRolesUserId) return;

    setRolesSaving(true);
    setRolesError('');

    try {
      const { data } = await axios.post(`/admin/users/${editingRolesUserId}/roles`, {
        roles: rolesDraft,
      });

      const updatedUser = data.user || data;

      setUsers((prev) =>
        prev.map((user) => (user.id === updatedUser.id ? { ...user, ...updatedUser } : user)),
      );
      setNotice('Roles updated successfully.');
      cancelRolesEditing();
    } catch (err) {
      const message = err.response?.data?.message || 'Unable to update roles right now.';
      setRolesError(message);
    } finally {
      setRolesSaving(false);
    }
  };

  const startEmailEditing = (user) => {
    setEditingEmailUserId(user.id);
    setEmailDraft(user.email || '');
    setEmailError('');
  };

  const cancelEmailEditing = () => {
    setEditingEmailUserId(null);
    setEmailDraft('');
    setEmailSaving(false);
    setEmailError('');
  };

  const saveEmail = async () => {
    if (!editingEmailUserId) return;
    const trimmedEmail = emailDraft.trim();
    if (!trimmedEmail) {
      setEmailError('Email is required.');
      return;
    }

    const currentUser = users.find((user) => user.id === editingEmailUserId);
    if (currentUser?.email === trimmedEmail) {
      setNotice('Email is unchanged.');
      cancelEmailEditing();
      return;
    }

    setEmailSaving(true);
    setEmailError('');

    try {
      const { data } = await axios.put(`/admin/users/${editingEmailUserId}`, {
        email: trimmedEmail,
      });
      const updatedUser = data.user || data;
      setUsers((prev) =>
        prev.map((user) => (user.id === updatedUser.id ? { ...user, ...updatedUser } : user)),
      );
      setNotice('Email updated successfully.');
      cancelEmailEditing();
    } catch (err) {
      const message =
        err.response?.data?.message ||
        Object.values(err.response?.data?.errors || {})?.[0]?.[0] ||
        'Unable to update email right now.';
      setEmailError(message);
    } finally {
      setEmailSaving(false);
    }
  };

  const openPasswordModal = (user) => {
    if (!canResetPasswords) return;
    setPasswordModalUser(user);
    setPasswordDraft('');
    setPasswordConfirmationDraft('');
    setShowPasswordDraft(false);
    setShowPasswordConfirmationDraft(false);
    setResetError('');
  };

  const closePasswordModal = () => {
    setPasswordModalUser(null);
    setPasswordDraft('');
    setPasswordConfirmationDraft('');
    setShowPasswordDraft(false);
    setShowPasswordConfirmationDraft(false);
    setResetError('');
  };

  const savePassword = async (event) => {
    event.preventDefault();
    if (!passwordModalUser) return;

    if (passwordDraft !== passwordConfirmationDraft) {
      setResetError('Passwords do not match.');
      return;
    }

    if (passwordDraft.length < 8) {
      setResetError('Password must be at least 8 characters.');
      return;
    }

    setPasswordSaving(true);
    setResetError('');

    try {
      const { data } = await axios.post(`/admin/users/${passwordModalUser.id}/password`, {
        password: passwordDraft,
        password_confirmation: passwordConfirmationDraft,
      });
      setNotice(data.message || `Password updated for ${passwordModalUser.email}.`);
      closePasswordModal();
    } catch (err) {
      const message =
        err.response?.data?.message ||
        Object.values(err.response?.data?.errors || {})?.[0]?.[0] ||
        'Unable to set password right now.';
      setResetError(message);
    } finally {
      setPasswordSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="card" style={{ textAlign: 'center' }}>
        Fetching people & permissions...
      </div>
    );
  }

  if (error) {
    return <div className="error-banner">{error}</div>;
  }

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ fontSize: '1.4rem' }}>People directory</h2>
          <p className="text-muted">
            Single source of truth for every workspace member.
            {canEditRoles ? ' Super admins can amend access levels here.' : ''}
          </p>
        </div>
        <button type="button" className="btn btn-primary" onClick={loadData}>
          Refresh
        </button>
      </div>

      {notice && <div className="success-banner">{notice}</div>}
      {rolesError && editingRolesUserId && <div className="error-banner">{rolesError}</div>}
      {emailError && editingEmailUserId && <div className="error-banner">{emailError}</div>}
      {resetError && !passwordModalUser && <div className="error-banner">{resetError}</div>}

      {users.length ? (
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Roles</th>
              <th>Status</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => {
              const status = statusPalette[user.status] || statusPalette.inactive;
              const isEditingRoles = editingRolesUserId === user.id;
              const isEditingEmail = editingEmailUserId === user.id;
              return (
                <tr key={user.id}>
                  <td>
                    <strong>{user.name}</strong>
                    <p style={{ fontSize: '0.85rem' }}>
                      Last login:{' '}
                      {user.last_login_at
                        ? new Date(user.last_login_at).toLocaleString()
                        : 'Never'}
                    </p>
                  </td>
                  <td>
                    {isEditingEmail ? (
                      <input
                        type="email"
                        className="input"
                        value={emailDraft}
                        onChange={(event) => setEmailDraft(event.target.value)}
                        disabled={emailSaving}
                        placeholder="name@example.com"
                      />
                    ) : (
                      user.email
                    )}
                  </td>
                  <td>
                    {isEditingRoles ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                          {roles.map((role) => (
                            <label
                              key={role.id}
                              className="badge badge-muted"
                              style={{ textTransform: 'capitalize', cursor: 'pointer' }}
                            >
                              <input
                                type="checkbox"
                                checked={rolesDraft.includes(role.id)}
                                onChange={() => toggleRoleDraft(role.id)}
                                disabled={rolesSaving}
                                style={{ marginRight: '0.35rem' }}
                              />
                              {role.slug}
                            </label>
                          ))}
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={saveRoles}
                            disabled={rolesSaving}
                          >
                            {rolesSaving ? 'Saving...' : 'Save roles'}
                          </button>
                          <button
                            type="button"
                            className="btn btn-muted"
                            onClick={cancelRolesEditing}
                            disabled={rolesSaving}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : user.roles?.length ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                        {user.roles.map((role) => (
                          <span
                            key={role.id}
                            className="badge badge-muted"
                            style={{ textTransform: 'capitalize' }}
                          >
                            {role.slug}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted">No role</span>
                    )}
                  </td>
                  <td>
                    <span className={`badge ${status.tone}`}>{status.label}</span>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <div className="table-actions">
                      {canEditEmail && (
                        isEditingEmail ? (
                          <>
                            <button
                              type="button"
                              onClick={saveEmail}
                              disabled={emailSaving}
                            >
                              {emailSaving ? 'Saving...' : 'Save email'}
                            </button>
                            <button
                              type="button"
                              onClick={cancelEmailEditing}
                              disabled={emailSaving}
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            onClick={() => startEmailEditing(user)}
                            disabled={emailSaving}
                          >
                            Edit email
                          </button>
                        )
                      )}
                      {canEditRoles && (
                        <button
                          type="button"
                          onClick={() => startRolesEditing(user)}
                          disabled={isEditingRoles}
                        >
                          {isEditingRoles ? 'Editing…' : 'Edit roles'}
                        </button>
                      )}
                      {canResetPasswords && (
                        <button
                          type="button"
                          onClick={() => openPasswordModal(user)}
                          disabled={passwordSaving && passwordModalUser?.id === user.id}
                        >
                          Set password
                        </button>
                      )}
                      <button type="button" className="danger">
                        Suspend
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <div className="card empty-state">
          <h3>No team members yet</h3>
          <p>
            Once users sign up, you'll see their access level, status, and last activity right here.
          </p>
        </div>
      )}

      {passwordModalUser && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.48)',
            display: 'grid',
            placeItems: 'center',
            padding: '1rem',
            zIndex: 1000,
          }}
        >
          <div className="card" style={{ width: 'min(100%, 520px)', display: 'grid', gap: '1rem' }}>
            <div>
              <h3 style={{ marginBottom: '0.35rem' }}>Set password</h3>
              <p className="text-muted" style={{ margin: 0 }}>
                Set a new password for <strong>{passwordModalUser.email}</strong>.
              </p>
            </div>

            <form onSubmit={savePassword} style={{ display: 'grid', gap: '0.85rem' }}>
              <div className="field">
                <label htmlFor="new-password">New password</label>
                <div className="input-with-action">
                  <input
                    id="new-password"
                    type={showPasswordDraft ? 'text' : 'password'}
                    className="input"
                    value={passwordDraft}
                    onChange={(event) => setPasswordDraft(event.target.value)}
                    minLength={8}
                    required
                    autoFocus
                  />
                  <button
                    type="button"
                    className="input-action"
                    onClick={() => setShowPasswordDraft((prev) => !prev)}
                    aria-label={showPasswordDraft ? 'Hide password' : 'Show password'}
                    title={showPasswordDraft ? 'Hide password' : 'Show password'}
                  >
                    <PasswordVisibilityIcon visible={showPasswordDraft} />
                  </button>
                </div>
              </div>

              <div className="field">
                <label htmlFor="confirm-password">Confirm password</label>
                <div className="input-with-action">
                  <input
                    id="confirm-password"
                    type={showPasswordConfirmationDraft ? 'text' : 'password'}
                    className="input"
                    value={passwordConfirmationDraft}
                    onChange={(event) => setPasswordConfirmationDraft(event.target.value)}
                    minLength={8}
                    required
                  />
                  <button
                    type="button"
                    className="input-action"
                    onClick={() => setShowPasswordConfirmationDraft((prev) => !prev)}
                    aria-label={showPasswordConfirmationDraft ? 'Hide password confirmation' : 'Show password confirmation'}
                    title={showPasswordConfirmationDraft ? 'Hide password confirmation' : 'Show password confirmation'}
                  >
                    <PasswordVisibilityIcon visible={showPasswordConfirmationDraft} />
                  </button>
                </div>
              </div>

              {resetError && <div className="error-banner" style={{ margin: 0 }}>{resetError}</div>}

              <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-muted"
                  onClick={closePasswordModal}
                  disabled={passwordSaving}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={passwordSaving}>
                  {passwordSaving ? 'Saving...' : 'Save password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

export default UserList;
